import { useState, useCallback } from 'react';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useVoiceSipTrunks, useVoiceSipTrunkMutations } from '@/hooks/useVoiceAgents';
import { SipTrunkForm } from '@/components/voiceAgents/SipTrunkForm';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Cable, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ICreateSipTrunkFormData, IVoiceSipTrunk } from '@/@types/voiceAgents';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up

type TDialogMode = { type: 'closed' } | { type: 'add' } | { type: 'edit'; trunk: IVoiceSipTrunk }; // LOCAL_TYPE_JUSTIFIED: page-local discriminated union, not reused elsewhere

export function VoiceSipTrunksPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { data: sipTrunks, isLoading: trunksLoading } = useVoiceSipTrunks(currentOrg?._id);
  const { create, update, remove } = useVoiceSipTrunkMutations();

  const [dialogMode, setDialogMode] = useState<TDialogMode>({ type: 'closed' });
  const [deleteTarget, setDeleteTarget] = useState<Id<'voiceSipTrunks'> | null>(null);

  const isLoading = orgLoading || trunksLoading;
  const trunks: IVoiceSipTrunk[] = sipTrunks ?? [];

  const handleCreate = useCallback(
    async (data: ICreateSipTrunkFormData) => {
      if (!currentOrg) return;
      await create({ ...data, orgId: currentOrg._id });
      setDialogMode({ type: 'closed' });
    },
    [currentOrg, create]
  );

  const handleUpdate = useCallback(
    async (data: ICreateSipTrunkFormData) => {
      if (dialogMode.type !== 'edit') return;
      await update({ id: dialogMode.trunk._id, ...data });
      setDialogMode({ type: 'closed' });
    },
    [dialogMode, update]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await remove({ id: deleteTarget });
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  const handleDialogClose = useCallback((open: boolean) => {
    if (!open) setDialogMode({ type: 'closed' });
  }, []);

  const handleDeleteDialogClose = useCallback((open: boolean) => {
    if (!open) setDeleteTarget(null);
  }, []);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Cable className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage SIP trunks.
          </p>
        </div>
      </div>
    );
  }

  const editInitialData: ICreateSipTrunkFormData | undefined =
    dialogMode.type === 'edit'
      ? {
          name: dialogMode.trunk.name,
          provider: dialogMode.trunk.provider,
          sipDomain: dialogMode.trunk.sipDomain,
          authUsername: dialogMode.trunk.authUsername,
          authPassword: dialogMode.trunk.authPassword,
        }
      : undefined;

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">SIP Trunks</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${trunks.length} trunk${trunks.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button onClick={() => setDialogMode({ type: 'add' })}>
          <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
          Add SIP Trunk
        </Button>
      </div>

      {/* Table */}
      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>SIP Domain</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                  Loading SIP trunks...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && trunks.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16">
                  <Cable className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No SIP trunks configured yet</p>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              trunks.map(trunk => (
                <TableRow key={trunk._id}>
                  <TableCell className="font-medium">{trunk.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {trunk.provider}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{trunk.sipDomain}</TableCell>
                  <TableCell>
                    <Badge variant={trunk.isActive ? 'default' : 'secondary'}>
                      {trunk.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDialogMode({ type: 'edit', trunk })}
                        title="Edit trunk"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(trunk._id)}
                        title="Delete trunk"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogMode.type !== 'closed'} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode.type === 'edit' ? 'Edit SIP Trunk' : 'Add SIP Trunk'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode.type === 'edit'
                ? 'Update the SIP trunk configuration.'
                : 'Configure a new SIP trunk for voice connectivity.'}
            </DialogDescription>
          </DialogHeader>
          <SipTrunkForm
            initialData={editInitialData}
            onSubmit={dialogMode.type === 'edit' ? handleUpdate : handleCreate}
            onCancel={() => setDialogMode({ type: 'closed' })}
            isSubmitting={false}
            submitLabel={dialogMode.type === 'edit' ? 'Save Changes' : 'Add Trunk'}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={handleDeleteDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SIP Trunk?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the SIP trunk. Phone numbers using this trunk will lose
              connectivity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
